import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import { apiPost } from '../utils/api';
import '../styles/SubmissionReport.css';

const SubmissionReportPage = () => {
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [report, setReport] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Helper function to truncate text and show tooltip
    const truncateText = (text, maxLength = 50) => {
        if (!text) return '';
        const textStr = String(text);
        if (textStr.length > maxLength) {
            return textStr.substring(0, maxLength) + '...';
        }
        return textStr;
    };

    // Helper function to get the full text for tooltip
    const getFullText = (text) => {
        return String(text || '');
    };

    // Helper function to format date as yyyy-mm-dd HH:MM:SS
    const formatDate = (dateInput) => {
        if (dateInput === null || dateInput === undefined || dateInput === '') return '-';
        const d = new Date(dateInput);
        if (isNaN(d.getTime())) return '-';
        const pad = (n) => String(n).padStart(2, '0');
        const yyyy = d.getFullYear();
        const mm = pad(d.getMonth() + 1);
        const dd = pad(d.getDate());
        const HH = pad(d.getHours());
        const MM = pad(d.getMinutes());
        const SS = pad(d.getSeconds());
        return `${yyyy}-${mm}-${dd} ${HH}:${MM}:${SS}`;
    };

    // Helper function to get delivered time from status history
    const getDeliveredTime = (statusHistory) => {
        if (!statusHistory || !Array.isArray(statusHistory)) return '-';
        const delivered = statusHistory.find(status => status.status === 'DELIVERED');
        if (delivered) return formatDate(delivered.time);
        const read = statusHistory.find(status => status.status === 'READ' || status.status === 'PLAYED');
        return read ? formatDate(read.time) : '-';
    };

    // Helper function to get read time from status history
    const getReadTime = (statusHistory) => {
        if (!statusHistory || !Array.isArray(statusHistory)) return '-';
        const read = statusHistory.find(status => status.status === 'READ' || status.status === 'PLAYED');
        return read ? formatDate(read.time) : '-';
    };

    // Download CSV function
    const downloadCSV = () => {
        if (report.length === 0) {
            alert('No data to download');
            return;
        }

        const headers = ['Mobile', 'Sent Time', 'Msg Type', 'Message', 'Media', 'Message ID', 'Delivered At', 'Read At'];
        const rows = report.map(item => [
            item.mobile || '',
            formatDate(item.insertDateTime),
            item.messageType || '',
            item.message || '',
            item.mediaPath || '',
            item.messageId || '',
            getDeliveredTime(item.statusHistory),
            getReadTime(item.statusHistory)
        ]);

        let csvContent = headers.join(',') + '\n';
        rows.forEach(row => {
            csvContent += row.map(cell => {
                // Escape quotes and wrap in quotes if contains comma or quote
                const cellStr = String(cell);
                if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                    return '"' + cellStr.replace(/"/g, '""') + '"';
                }
                return cellStr;
            }).join(',') + '\n';
        });

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'SubmissionReport.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        setSearched(true);

        try {
            const wano = sessionStorage.getItem("wano") || "919988778899";
            const userId = sessionStorage.getItem('username') || 'admin';
            const data = await apiPost('/MessageReport/SubmissionReport', {
                UserId: userId,
                FromDate: new Date(fromDate),
                ToDate: new Date(toDate),
                wano: wano
            });
            setReport(data);
        } catch (err) {
            setError(err.message || 'An error occurred while fetching the report');
            console.error('Failed to load submission report', err);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFromDate('');
        setToDate('');
        setReport([]);
        setError('');
        setSearched(false);
        setCurrentPage(1);
    };

    return (
        <>
            <Navbar />
            <div className="submission-report-container">
            <div className="report-header">
                <h1>Submission Report</h1>
                {/* <p className="report-subtitle">Generate detailed reports for message submissions</p> */}
            </div>

            <div className="report-content">
                {/* Filter Section */}
                <div className="filter-section">
                    <form onSubmit={handleSubmit} className="filter-form">
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="fromDate">From Date</label>
                                <input
                                    type="date"
                                    id="fromDate"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    required
                                    className="date-input"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="toDate">To Date</label>
                                <input
                                    type="date"
                                    id="toDate"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    required
                                    className="date-input"
                                />
                            </div>
                        </div>
                        <div className="button-group">
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                {loading ? 'Loading...' : 'Generate Report'}
                            </button>
                            <button type="button" className="btn btn-secondary" onClick={handleReset}>
                                Reset
                            </button>
                        </div>
                    </form>
                </div>

                {/* Error Message */}
                {error && <div className="error-message">{error}</div>}

                {/* Results Section */}
                {searched && (
                    <div className="results-section">
                        <div className="results-header">
                            <h2>Report Results</h2>
                            {!loading && report.length > 0 && (
                                <button className="btn btn-download" onClick={downloadCSV}>
                                    ⬇ Download CSV
                                </button>
                            )}
                        </div>
                        {loading && (
                            <div className="loading-message">
                                <div className="spinner"></div>
                                <p>Loading report data...</p>
                            </div>
                        )}
                        {!loading && report.length === 0 && !error && (
                            <div className="no-data-message">
                                <p>No data found for the selected date range.</p>
                            </div>
                        )}
                        {!loading && report.length > 0 && (
                            <>
                                <div className="table-wrapper">
                                    <table className="report-table">
                                        <thead>
                                            <tr>
                                                <th>Mobile</th>
                                                <th>Sent Time</th>
                                                <th>Msg Type</th>
                                                <th>Message</th>
                                                <th>Media</th>
                                                <th>Message ID</th>
                                                <th>Delivered At</th>
                                                <th>Read At</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {report.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((item, index) => (
                                                <tr key={index}>
                                                    <td>{item.mobile}</td>
                                                    <td title={getFullText(item.insertDateTime)}>
                                                        {formatDate(item.insertDateTime)}
                                                    </td>
                                                    <td title={getFullText(item.messageType)}>
                                                        {truncateText(item.messageType, 50)}
                                                    </td>
                                                    <td title={getFullText(item.message)}>
                                                        {truncateText(item.message, 50)}
                                                    </td>
                                                    <td title={getFullText(item.mediaPath)}>
                                                        {truncateText(item.mediaPath, 50)}
                                                    </td>
                                                    <td title={getFullText(item.messageId)}>
                                                        {truncateText(item.messageId, 50)}
                                                    </td>
                                                    <td title={getFullText(getDeliveredTime(item.statusHistory))}>
                                                        {truncateText(getDeliveredTime(item.statusHistory), 50)}
                                                    </td>
                                                    <td title={getFullText(getReadTime(item.statusHistory))}>
                                                        {truncateText(getReadTime(item.statusHistory), 50)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                <div className="pagination">
                                    <button 
                                        className="pagination-btn" 
                                        onClick={() => setCurrentPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        ← Previous
                                    </button>
                                    <span className="pagination-info">
                                        Page {currentPage} of {Math.ceil(report.length / itemsPerPage)} 
                                        ({report.length} total records)
                                    </span>
                                    <button 
                                        className="pagination-btn" 
                                        onClick={() => setCurrentPage(currentPage + 1)}
                                        disabled={currentPage >= Math.ceil(report.length / itemsPerPage)}
                                    >
                                        Next →
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
            </div>
        </>
    );
};

export default SubmissionReportPage;